import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FaCode, FaRocket, FaHeart, FaLightbulb } from 'react-icons/fa';
import './About.css';

const About = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -15 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      y: -10,
      rotateY: 5,
      transition: {
        duration: 0.3
      }
    }
  };

  const stats = [
    { number: "5+", label: "Projects Completed" },
    { number: "6 months", label: "Experience" },
    { number: "15+", label: "Technologies Mastered" },
    { number: "100%", label: "Client Satisfaction" }
  ];

  const features = [
    {
      icon: <FaCode />,
      title: "Clean Code",
      description: "I write maintainable, scalable, and efficient code following best practices and industry standards."
    },
    {
      icon: <FaRocket />,
      title: "Fast Delivery",
      description: "I deliver projects on time without compromising quality, ensuring your business goals are met."
    },
    {
      icon: <FaHeart />,
      title: "Passion Driven",
      description: "I'm passionate about creating amazing digital experiences that make a real difference."
    },
    {
      icon: <FaLightbulb />,
      title: "Innovation",
      description: "I stay updated with the latest technologies and trends to provide cutting-edge solutions."
    }
  ];

  return (
    <section id="about" className="about">
      <div className="container">
        <motion.div
          className="about-header"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.h2 className="section-title" variants={itemVariants}>
            About Me
          </motion.h2>
          <motion.p className="section-subtitle" variants={itemVariants}>
            Get to know me better
          </motion.p>
        </motion.div>

        <div className="about-content">
          <motion.div
            className="about-text"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="about-card" variants={itemVariants}>
              <h3>Who I Am</h3>
              <p>
                I'm Ransa Nilakshaya Abeywickrama (also known as Ransa N Abeywickrama or rnabeywickrama), 
                a passionate Full Stack Developer with expertise in building dynamic and responsive web 
                applications. My journey in software development has been driven by curiosity and a deep 
                passion for solving complex problems through elegant, efficient solutions.
              </p>
              <p>
                With a strong foundation in modern web technologies, I bring ideas to life through 
                clean code, beautiful design, and innovative approaches. I'm committed to creating 
                digital experiences that not only look great but also deliver exceptional performance 
                and user satisfaction. I'm available for freelance projects and ready to help you 
                build your next web application.
              </p>
            </motion.div>

            <motion.div className="about-card" variants={itemVariants}>
              <h3>My Expertise</h3>
              <p>
                I specialize in Full Stack Development with expertise in React, Node.js, JavaScript, 
                and cloud technologies. My technical skills span across frontend frameworks, backend 
                architectures, and database management systems.
              </p>
              <p>
                I'm always eager to learn new technologies and take on challenging projects that push 
                the boundaries of web development. My approach combines technical excellence with 
                creative problem-solving to deliver solutions that exceed expectations.
              </p>
            </motion.div>
          </motion.div>

          <motion.div
            className="about-stats"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                className="stat-card"
                variants={cardVariants}
                whileHover="hover"
                ref={ref}
              >
                <motion.div 
                  className="stat-number"
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : { scale: 0 }}
                  transition={{ delay: index * 0.15 + 0.2, type: "spring", stiffness: 200 }}
                >
                  {stat.number}
                </motion.div>
                <div className="stat-label">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="features-grid"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              className="feature-card"
              variants={cardVariants}
              whileHover="hover"
            >
              <div className="feature-icon">
                {feature.icon}
              </div>
              <h4>{feature.title}</h4>
              <p>{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default About;
